
import { PythonModule } from './types';

export const PYTHON_DECORATORS_GENERATORS_MODULE: PythonModule = {
  id: "python-decorators-generators",
  title: "Decorators, Generators, and Advanced Functions",
  description: "Master Python's advanced function features including decorators, generators, and functional programming concepts.",
  difficulty: "advanced",
  content: `
# Decorators, Generators, and Advanced Functions

Advanced Python features that enable elegant, efficient, and reusable code patterns.

## Understanding Decorators

Decorators are a way to modify or enhance functions and classes without permanently modifying their structure.

### Function Decorators

\`\`\`python
def my_decorator(func):
    def wrapper(*args, **kwargs):
        print(f"Calling function: {func.__name__}")
        result = func(*args, **kwargs)
        print(f"Function {func.__name__} completed")
        return result
    return wrapper

@my_decorator
def greet(name):
    return f"Hello, {name}!"

# Usage
message = greet("Alice")  # Prints decorator messages
print(message)  # Hello, Alice!
\`\`\`

### Decorators with Parameters

\`\`\`python
def repeat(times):
    def decorator(func):
        def wrapper(*args, **kwargs):
            for i in range(times):
                result = func(*args, **kwargs)
            return result
        return wrapper
    return decorator

@repeat(3)
def say_hello():
    print("Hello!")

say_hello()  # Prints "Hello!" three times
\`\`\`

### Class Decorators

\`\`\`python
def add_string_method(cls):
    def __str__(self):
        return f"{cls.__name__} instance"
    cls.__str__ = __str__
    return cls

@add_string_method
class MyClass:
    pass

obj = MyClass()
print(obj)  # MyClass instance
\`\`\`

## Generators and Iterators

Generators provide memory-efficient ways to create iterators.

### Basic Generators

\`\`\`python
def count_up_to(n):
    count = 1
    while count <= n:
        yield count
        count += 1

# Usage
counter = count_up_to(5)
for num in counter:
    print(num)  # Prints 1, 2, 3, 4, 5
\`\`\`

### Generator Expressions

\`\`\`python
# Generator expression
squares = (x**2 for x in range(10))
print(list(squares))  # [0, 1, 4, 9, 16, 25, 36, 49, 64, 81]

# Memory efficient file processing
def process_large_file(filename):
    with open(filename, 'r') as file:
        for line in file:
            yield line.strip().upper()

# Usage
for processed_line in process_large_file('data.txt'):
    print(processed_line)
\`\`\`

### Advanced Generator Patterns

\`\`\`python
def fibonacci():
    a, b = 0, 1
    while True:
        yield a
        a, b = b, a + b

# Get first 10 Fibonacci numbers
fib = fibonacci()
first_ten = [next(fib) for _ in range(10)]
print(first_ten)  # [0, 1, 1, 2, 3, 5, 8, 13, 21, 34]
\`\`\`

## Functional Programming Concepts

### Lambda Functions

\`\`\`python
# Basic lambda
square = lambda x: x**2
print(square(5))  # 25

# Lambda with multiple arguments
add = lambda x, y: x + y
print(add(3, 4))  # 7

# Using lambda with built-in functions
numbers = [1, 2, 3, 4, 5]
squared = list(map(lambda x: x**2, numbers))
evens = list(filter(lambda x: x % 2 == 0, numbers))
\`\`\`

### Higher-Order Functions

\`\`\`python
def apply_operation(func, *args):
    return func(*args)

def multiply(x, y):
    return x * y

result = apply_operation(multiply, 4, 5)  # 20

# Function returning function
def make_multiplier(n):
    return lambda x: x * n

double = make_multiplier(2)
triple = make_multiplier(3)

print(double(10))  # 20
print(triple(10))  # 30
\`\`\`

### functools Module

\`\`\`python
from functools import reduce, partial, wraps

# reduce function
numbers = [1, 2, 3, 4, 5]
sum_all = reduce(lambda x, y: x + y, numbers)
print(sum_all)  # 15

# partial functions
def multiply(x, y, z):
    return x * y * z

multiply_by_2 = partial(multiply, 2)
result = multiply_by_2(3, 4)  # 2 * 3 * 4 = 24

# wraps decorator for preserving function metadata
def timer_decorator(func):
    @wraps(func)
    def wrapper(*args, **kwargs):
        import time
        start = time.time()
        result = func(*args, **kwargs)
        end = time.time()
        print(f"{func.__name__} took {end - start:.4f} seconds")
        return result
    return wrapper
\`\`\`

## Practical Applications

### Caching Decorator

\`\`\`python
from functools import wraps

def memoize(func):
    cache = {}
    
    @wraps(func)
    def wrapper(*args):
        if args in cache:
            return cache[args]
        result = func(*args)
        cache[args] = result
        return result
    return wrapper

@memoize
def expensive_calculation(n):
    print(f"Computing for {n}")
    return n ** 2

print(expensive_calculation(5))  # Computes and caches
print(expensive_calculation(5))  # Returns from cache
\`\`\`

### Data Pipeline with Generators

\`\`\`python
def read_data():
    data = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    for item in data:
        yield item

def filter_even(data):
    for item in data:
        if item % 2 == 0:
            yield item

def square_values(data):
    for item in data:
        yield item ** 2

# Create processing pipeline
pipeline = square_values(filter_even(read_data()))
result = list(pipeline)
print(result)  # [4, 16, 36, 64, 100]
\`\`\`

## Best Practices

1. **Use generators for memory efficiency** when working with large datasets
2. **Keep decorators simple** and focused on single responsibilities
3. **Use functools.wraps** to preserve function metadata
4. **Consider performance implications** of decorators in critical code
5. **Document decorator behavior** clearly for maintainability

## Practice Exercise

Create a performance monitoring system using decorators and generators that tracks function execution times and generates reports.
`,
  duration: "75 minutes",
  objectives: [
    "Master decorator patterns and applications",
    "Understand generators and memory-efficient iteration",
    "Apply functional programming concepts in Python",
    "Create reusable decorators for common tasks",
    "Build efficient data processing pipelines"
  ],
  order: 1
};

export const PYTHON_ASYNC_PROGRAMMING_MODULE: PythonModule = {
  id: "python-async-programming",
  title: "Asynchronous Programming and Concurrency",
  description: "Learn async/await, coroutines, and concurrent programming to build high-performance Python applications.",
  difficulty: "advanced",
  content: `
# Asynchronous Programming and Concurrency

Modern Python applications require efficient handling of I/O operations, network requests, and concurrent tasks.

## Understanding Asynchronous Programming

Asynchronous programming allows your program to handle multiple operations concurrently without blocking.

### Basic Async/Await

\`\`\`python
import asyncio
import time

async def say_hello(name, delay):
    print(f"Hello {name}, starting...")
    await asyncio.sleep(delay)  # Non-blocking sleep
    print(f"Hello {name}, finished after {delay} seconds!")
    return f"Completed: {name}"

async def main():
    # Sequential execution
    start_time = time.time()
    result1 = await say_hello("Alice", 2)
    result2 = await say_hello("Bob", 1)
    end_time = time.time()
    print(f"Sequential took: {end_time - start_time:.2f} seconds")

# Run the async function
asyncio.run(main())
\`\`\`

### Concurrent Execution

\`\`\`python
async def concurrent_main():
    start_time = time.time()
    
    # Run tasks concurrently
    task1 = asyncio.create_task(say_hello("Alice", 2))
    task2 = asyncio.create_task(say_hello("Bob", 1))
    
    results = await asyncio.gather(task1, task2)
    
    end_time = time.time()
    print(f"Concurrent took: {end_time - start_time:.2f} seconds")
    print(f"Results: {results}")

asyncio.run(concurrent_main())
\`\`\`

## Working with HTTP Requests

### Async HTTP Client

\`\`\`python
import aiohttp
import asyncio

async def fetch_url(session, url):
    try:
        async with session.get(url) as response:
            content = await response.text()
            return {
                'url': url,
                'status': response.status,
                'length': len(content)
            }
    except Exception as e:
        return {'url': url, 'error': str(e)}

async def fetch_multiple_urls(urls):
    async with aiohttp.ClientSession() as session:
        tasks = [fetch_url(session, url) for url in urls]
        results = await asyncio.gather(*tasks)
        return results

# Usage
urls = [
    'https://httpbin.org/delay/1',
    'https://httpbin.org/delay/2',
    'https://httpbin.org/json'
]

results = asyncio.run(fetch_multiple_urls(urls))
for result in results:
    print(result)
\`\`\`

## Async Context Managers

\`\`\`python
class AsyncDatabaseConnection:
    def __init__(self, connection_string):
        self.connection_string = connection_string
        self.connection = None
    
    async def __aenter__(self):
        print("Connecting to database...")
        await asyncio.sleep(1)  # Simulate connection time
        self.connection = f"Connected to {self.connection_string}"
        return self
    
    async def __aexit__(self, exc_type, exc_val, exc_tb):
        print("Closing database connection...")
        await asyncio.sleep(0.5)  # Simulate cleanup time
        self.connection = None
    
    async def execute_query(self, query):
        if not self.connection:
            raise Exception("Not connected to database")
        print(f"Executing: {query}")
        await asyncio.sleep(0.5)  # Simulate query time
        return f"Result for: {query}"

async def database_operations():
    async with AsyncDatabaseConnection("postgresql://localhost") as db:
        result1 = await db.execute_query("SELECT * FROM users")
        result2 = await db.execute_query("SELECT * FROM orders")
        return [result1, result2]

results = asyncio.run(database_operations())
\`\`\`

## Best Practices

1. **Use async/await for I/O-bound operations**
2. **Avoid blocking operations in async code**
3. **Handle exceptions properly in concurrent tasks**
4. **Use semaphores to limit concurrent operations**
5. **Clean up resources with async context managers**
6. **Profile and monitor async applications**

## Practice Exercise

Build an async file processor that downloads multiple files concurrently, processes them, and saves results to a database.
`,
  duration: "80 minutes",
  objectives: [
    "Understand asynchronous programming concepts",
    "Master async/await syntax and patterns",
    "Build concurrent applications with asyncio",
    "Handle errors in asynchronous code",
    "Apply async patterns to real-world problems"
  ],
  order: 2
};

export const PYTHON_WEB_DEVELOPMENT_MODULE: PythonModule = {
  id: "python-web-development",
  title: "Web Development with FastAPI and Django",
  description: "Build modern web applications and APIs using FastAPI and Django frameworks.",
  difficulty: "advanced",
  content: `
# Web Development with FastAPI and Django

Learn to build scalable web applications and APIs using Python's most popular web frameworks.

## FastAPI Fundamentals

FastAPI is a modern, fast web framework for building APIs with Python 3.7+.

### Basic FastAPI Application

\`\`\`python
from fastapi import FastAPI, HTTPException
from pydantic import BaseModel
from typing import List, Optional
import uvicorn

app = FastAPI(title="My API", version="1.0.0")

# Pydantic models for request/response
class Item(BaseModel):
    id: Optional[int] = None
    name: str
    price: float
    description: Optional[str] = None

class ItemCreate(BaseModel):
    name: str
    price: float
    description: Optional[str] = None

# In-memory storage (use database in production)
items_db = []
next_id = 1

@app.get("/")
async def root():
    return {"message": "Welcome to my API"}

@app.get("/items", response_model=List[Item])
async def get_items():
    return items_db

@app.get("/items/{item_id}", response_model=Item)
async def get_item(item_id: int):
    for item in items_db:
        if item.id == item_id:
            return item
    raise HTTPException(status_code=404, detail="Item not found")

@app.post("/items", response_model=Item)
async def create_item(item: ItemCreate):
    global next_id
    new_item = Item(id=next_id, **item.dict())
    items_db.append(new_item)
    next_id += 1
    return new_item

if __name__ == "__main__":
    uvicorn.run(app, host="0.0.0.0", port=8000)
\`\`\`

## Django Basics

Django is a high-level Python web framework that encourages rapid development.

### Django Project Structure

\`\`\`
myproject/
    manage.py
    myproject/
        __init__.py
        settings.py
        urls.py
        wsgi.py
    myapp/
        __init__.py
        admin.py
        apps.py
        models.py
        tests.py
        views.py
        urls.py
        migrations/
\`\`\`

### Django Models

\`\`\`python
# models.py
from django.db import models
from django.contrib.auth.models import User

class Category(models.Model):
    name = models.CharField(max_length=100)
    description = models.TextField(blank=True)
    created_at = models.DateTimeField(auto_now_add=True)

    def __str__(self):
        return self.name

class Product(models.Model):
    name = models.CharField(max_length=200)
    description = models.TextField()
    price = models.DecimalField(max_digits=10, decimal_places=2)
    category = models.ForeignKey(Category, on_delete=models.CASCADE)
    created_by = models.ForeignKey(User, on_delete=models.CASCADE)
    created_at = models.DateTimeField(auto_now_add=True)
    updated_at = models.DateTimeField(auto_now=True)

    def __str__(self):
        return self.name
\`\`\`

### Django Views

\`\`\`python
# views.py
from django.shortcuts import render, get_object_or_404
from django.http import JsonResponse
from django.views.decorators.csrf import csrf_exempt
import json

def product_list(request):
    products = Product.objects.all()
    return render(request, 'products/list.html', {'products': products})

def product_detail(request, product_id):
    product = get_object_or_404(Product, id=product_id)
    return render(request, 'products/detail.html', {'product': product})

@csrf_exempt
def api_products(request):
    if request.method == 'GET':
        products = Product.objects.all()
        data = [
            {
                'id': p.id,
                'name': p.name,
                'price': str(p.price),
                'category': p.category.name
            }
            for p in products
        ]
        return JsonResponse({'products': data})
    
    elif request.method == 'POST':
        data = json.loads(request.body)
        # Create new product logic here
        return JsonResponse({'status': 'created'})
\`\`\`

### Django Templates

\`\`\`html
<!-- templates/products/list.html -->
<!DOCTYPE html>
<html>
<head>
    <title>Products</title>
    <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">
</head>
<body>
    <div class="container">
        <h1>Products</h1>
        <div class="row">
            {% for product in products %}
                <div class="col-md-4 mb-3">
                    <div class="card">
                        <div class="card-body">
                            <h5 class="card-title">{{ product.name }}</h5>
                            <p class="card-text">{{ product.description|truncatewords:20 }}</p>
                            <p class="card-text"><strong>{{ product.price }}</strong></p>
                            <a href="{% url 'product_detail' product.pk %}" class="btn btn-primary">View Details</a>
                        </div>
                    </div>
                </div>
            {% endfor %}
        </div>
    </div>
</body>
</html>
\`\`\`

## Building REST APIs

### FastAPI with Database

\`\`\`python
from fastapi import FastAPI, Depends, HTTPException
from sqlalchemy import create_engine, Column, Integer, String, Float
from sqlalchemy.ext.declarative import declarative_base
from sqlalchemy.orm import sessionmaker, Session

# Database setup
SQLALCHEMY_DATABASE_URL = "sqlite:///./test.db"
engine = create_engine(SQLALCHEMY_DATABASE_URL)
SessionLocal = sessionmaker(autocommit=False, autoflush=False, bind=engine)
Base = declarative_base()

# Database model
class ItemDB(Base):
    __tablename__ = "items"
    
    id = Column(Integer, primary_key=True, index=True)
    name = Column(String, index=True)
    price = Column(Float)
    description = Column(String)

Base.metadata.create_all(bind=engine)

# Dependency
def get_db():
    db = SessionLocal()
    try:
        yield db
    finally:
        db.close()

app = FastAPI()

@app.post("/items/")
async def create_item(item: ItemCreate, db: Session = Depends(get_db)):
    db_item = ItemDB(**item.dict())
    db.add(db_item)
    db.commit()
    db.refresh(db_item)
    return db_item

@app.get("/items/")
async def read_items(skip: int = 0, limit: int = 100, db: Session = Depends(get_db)):
    items = db.query(ItemDB).offset(skip).limit(limit).all()
    return items
\`\`\`

## Authentication and Security

### JWT Authentication with FastAPI

\`\`\`python
from fastapi import FastAPI, Depends, HTTPException, status
from fastapi.security import HTTPBearer, HTTPAuthorizationCredentials
from passlib.context import CryptContext
import jwt
from datetime import datetime, timedelta

app = FastAPI()
security = HTTPBearer()
pwd_context = CryptContext(schemes=["bcrypt"], deprecated="auto")

SECRET_KEY = "your-secret-key"
ALGORITHM = "HS256"
ACCESS_TOKEN_EXPIRE_MINUTES = 30

def verify_password(plain_password, hashed_password):
    return pwd_context.verify(plain_password, hashed_password)

def get_password_hash(password):
    return pwd_context.hash(password)

def create_access_token(data: dict):
    to_encode = data.copy()
    expire = datetime.utcnow() + timedelta(minutes=ACCESS_TOKEN_EXPIRE_MINUTES)
    to_encode.update({"exp": expire})
    encoded_jwt = jwt.encode(to_encode, SECRET_KEY, algorithm=ALGORITHM)
    return encoded_jwt

def verify_token(credentials: HTTPAuthorizationCredentials = Depends(security)):
    try:
        payload = jwt.decode(credentials.credentials, SECRET_KEY, algorithms=[ALGORITHM])
        username: str = payload.get("sub")
        if username is None:
            raise HTTPException(status_code=401, detail="Invalid token")
        return username
    except jwt.PyJWTError:
        raise HTTPException(status_code=401, detail="Invalid token")

@app.post("/login")
async def login(username: str, password: str):
    # Verify user credentials (implement your logic)
    if verify_user(username, password):  # You need to implement this
        access_token = create_access_token(data={"sub": username})
        return {"access_token": access_token, "token_type": "bearer"}
    raise HTTPException(status_code=401, detail="Invalid credentials")

@app.get("/protected")
async def protected_route(current_user: str = Depends(verify_token)):
    return {"message": f"Hello {current_user}, this is a protected route"}
\`\`\`

## Best Practices

1. **Use environment variables** for configuration
2. **Implement proper error handling** and logging
3. **Add input validation** and sanitization
4. **Use database migrations** for schema changes
5. **Implement comprehensive testing**
6. **Add API documentation** (FastAPI does this automatically)
7. **Use dependency injection** for better code organization

## Practice Exercise

Build a complete e-commerce API with user authentication, product management, and order processing using either FastAPI or Django.
`,
  duration: "90 minutes",
  objectives: [
    "Build web applications with FastAPI and Django",
    "Create REST APIs with proper authentication",
    "Implement database models and relationships",
    "Handle HTTP requests and responses",
    "Apply web security best practices"
  ],
  order: 3
};

// Additional modules would go here...

// Export all advanced modules
export const PYTHON_ADVANCED_MODULES = [
  PYTHON_DECORATORS_GENERATORS_MODULE,
  PYTHON_ASYNC_PROGRAMMING_MODULE,
  PYTHON_WEB_DEVELOPMENT_MODULE
];
